import {
  TrendingUp,
  AlertTriangle,
  Package,
  DollarSign,
  ShoppingCart,
  Users,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react'
import { useState, useEffect } from 'react'

import { KpiCard } from '../components/dashboard/KpiCard'
import { SalesTrendChart } from '../components/dashboard/SalesTrendChart'
import { BranchComparisonChart } from '../components/dashboard/BranchComparisonChart'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { Button } from '../components/ui/button'
import {
  dashboardService,
  type DashboardData,
} from '../services/dashboard.service'

const KPI_ICON_MAP: Record<string, { icon: LucideIcon; color: string; bgColor: string }> = {
  'Ventas Hoy': { icon: DollarSign, color: 'text-green-500', bgColor: 'bg-green-500/10' },
  'Utilidad del Día': { icon: TrendingUp, color: 'text-primary', bgColor: 'bg-primary/10' },
  'Productos Vendidos': { icon: ShoppingCart, color: 'text-purple-500', bgColor: 'bg-purple-500/10' },
  'Clientes Frecuentes Atendidos': { icon: Users, color: 'text-orange-500', bgColor: 'bg-orange-500/10' },
}

export function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())

  const [alertsPage, setAlertsPage] = useState(1)
  const [activityPage, setActivityPage] = useState(1)
  const alertsPerPage = 2
  const activityPerPage = 2

  const fetchDashboard = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await dashboardService.getDashboard()
      setData(result)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Error al obtener el dashboard:', err)
      setError('No fue posible cargar el dashboard.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboard()
  }, [])

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-slate-600">Cargando dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <p className="font-medium text-red-600">{error}</p>
          <Button className="mt-4" onClick={fetchDashboard}>
            Reintentar
          </Button>
        </div>
      </div>
    )
  }

  if (!data) return null

  const criticalAlerts = data.criticalAlerts
  const recentActivity = data.recentActivity

  const alertsFrom = (alertsPage - 1) * alertsPerPage
  const activityFrom = (activityPage - 1) * activityPerPage
  const paginatedAlerts = criticalAlerts.slice(alertsFrom, alertsFrom + alertsPerPage)
  const paginatedActivity = recentActivity.slice(activityFrom, activityFrom + activityPerPage)

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-foreground">Panel General</h1>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {lastUpdated.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} • {lastUpdated.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <Button onClick={fetchDashboard} variant="outline" className="bg-white">
          <RefreshCw className="mr-2 h-4 w-4" />
          Actualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {data.kpis.map((kpi) => {
          const meta = KPI_ICON_MAP[kpi.title] ?? {
            icon: DollarSign,
            color: 'text-primary',
            bgColor: 'bg-primary/10',
          }
          return (
            <KpiCard
              key={kpi.title}
              title={kpi.title}
              value={kpi.value}
              change={kpi.change}
              trend={kpi.trend}
              icon={meta.icon}
              color={meta.color}
              bgColor={meta.bgColor}
            />
          )
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SalesTrendChart data={data.salesData} />
        <BranchComparisonChart data={data.branchComparison} />
      </div>

      {/* Alerts and Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Critical Alerts */}
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="font-semibold text-card-foreground">Alertas Críticas</h3>
          </div>
          <div className="space-y-3">
            {paginatedAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`rounded-lg border p-3 ${alert.severity === 'high'
                    ? 'border-red-500/40 bg-red-500/10'
                    : alert.severity === 'medium'
                      ? 'border-yellow-500/40 bg-yellow-500/10'
                      : 'border-border bg-card/60'
                  }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-card-foreground">
                      {alert.product}
                    </p>
                    <p className="mt-1 text-xs text-card-description">
                      {alert.branch}
                    </p>
                    {alert.type === 'stock' && (
                      <p className="mt-1 text-xs text-card-description">
                        Stock actual:{' '}
                        <span className="font-medium">{alert.stock} unidades</span>
                      </p>
                    )}
                    {alert.type === 'expiry' && (
                      <p className="mt-1 text-xs text-card-description">
                        Vence en:{' '}
                        <span className="font-medium">{alert.days} días</span>
                      </p>
                    )}
                    {alert.type === 'noMovement' && (
                      <p className="mt-1 text-xs text-card-description">
                        Sin rotación:{' '}
                        <span className="font-medium">{alert.days} días</span>
                      </p>
                    )}
                  </div>
                  <Package
                    className={`h-4 w-4 ${alert.severity === 'high'
                        ? 'text-red-600'
                        : alert.severity === 'medium'
                          ? 'text-yellow-600'
                          : 'text-card-description'
                      }`}
                  />
                </div>
              </div>
            ))}
          </div>
          <GlobalPaginator
            totalItems={criticalAlerts.length}
            itemsPerPage={alertsPerPage}
            currentPage={alertsPage}
            from={criticalAlerts.length === 0 ? 0 : alertsFrom + 1}
            to={Math.min(alertsFrom + alertsPerPage, criticalAlerts.length)}
            onPageChange={setAlertsPage}
          />
        </div>

        {/* Recent Activity */}
        <div className="flex h-full flex-col rounded-lg border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold text-card-foreground">
            Actividad Reciente
          </h3>
          <div className="flex-1 space-y-4">
            {paginatedActivity.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3">
                <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
                <div className="flex-1">
                  <p className="text-sm text-card-foreground">
                    {activity.description}
                  </p>
                  <p className="mt-1 text-xs text-card-description">
                    {activity.user} • {activity.time}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <GlobalPaginator
              totalItems={recentActivity.length}
              itemsPerPage={activityPerPage}
              currentPage={activityPage}
              from={recentActivity.length === 0 ? 0 : activityFrom + 1}
              to={Math.min(activityFrom + activityPerPage, recentActivity.length)}
              onPageChange={setActivityPage}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

